import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Check, Sparkles, Gift, User, Send, Upload, Image as ImageIcon, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useNavigate } from 'react-router-dom';
type Category = 'balloons' | 'flowers' | 'giftbox' | null;
type RecipientType = 'myself' | 'gifting' | null;
type BalloonType = 'helium' | 'stick' | null;
type GiftBoxGender = 'man' | 'woman' | null;
interface CustomizationData {
  category: Category;
  recipientType: RecipientType;
  recipientName?: string;
  recipientPhone?: string;
  referenceImages: string[]; // Base64 image data URLs
  // Balloons
  balloonColors: string[];
  balloonMessage?: string;
  balloonType: BalloonType;
  // Flowers
  flowerInBox: boolean | null;
  flowerBoxMessage?: string;
  flowerType?: string;
  flowerColors: string[];
  // Gift Box
  giftBoxGender: GiftBoxGender;
  giftBoxWithBalloons: boolean | null;
  giftBoxBalloonColors: string[];
  giftBoxBalloonMessage?: string;
  giftBoxItems: string[];
}
interface CustomizationWizardProps {
  isOpen: boolean;
  onClose: () => void;
}
const BALLOON_COLORS = [{
  name: 'Red',
  hex: '#EF4444'
}, {
  name: 'Pink',
  hex: '#EC4899'
}, {
  name: 'Purple',
  hex: '#A855F7'
}, {
  name: 'Blue',
  hex: '#3B82F6'
}, {
  name: 'Green',
  hex: '#10B981'
}, {
  name: 'Yellow',
  hex: '#F59E0B'
}, {
  name: 'Orange',
  hex: '#F97316'
}, {
  name: 'White',
  hex: '#FFFFFF'
}, {
  name: 'Black',
  hex: '#1F2937'
}, {
  name: 'Gold',
  hex: '#FCD34D'
}, {
  name: 'Silver',
  hex: '#D1D5DB'
}, {
  name: 'Rose Gold',
  hex: '#F9A8D4'
}];
const FLOWER_TYPES = ['Roses', 'Tulips', 'Lilies', 'Sunflowers', 'Orchids', 'Carnations', 'Daisies', 'Peonies', 'Hydrangeas', 'Mixed Bouquet'];
const GIFT_BOX_ITEMS = ['Chocolates', 'Wine Bottle', 'Perfume', 'Candles', 'Teddy Bear', 'Greeting Card', 'Cookies', 'Champagne'];
export function CustomizationWizard({
  isOpen,
  onClose
}: CustomizationWizardProps) {
  const [step, setStep] = useState(1);
  const {
    isAuthenticated,
    user
  } = useAuth();
  const {
    addCustomOrderRequest
  } = useData();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [data, setData] = useState<CustomizationData>({
    category: null,
    recipientType: null,
    referenceImages: [],
    balloonColors: [],
    balloonType: null,
    flowerInBox: null,
    flowerColors: [],
    giftBoxGender: null,
    giftBoxWithBalloons: null,
    giftBoxBalloonColors: [],
    giftBoxItems: []
  });
  const totalSteps = data.category === 'balloons' ? 5 // Added photo upload step
  : data.category === 'flowers' ? 6 // Added photo upload step
  : data.category === 'giftbox' ? 7 // Added photo upload step
  : 3;
  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  const toggleColor = (colorName: string, maxColors: number = 3) => {
    const colors = data.category === 'giftbox' ? data.giftBoxBalloonColors : data.category === 'flowers' ? data.flowerColors : data.balloonColors;
    if (colors.includes(colorName)) {
      const newColors = colors.filter(c => c !== colorName);
      if (data.category === 'giftbox') {
        setData({
          ...data,
          giftBoxBalloonColors: newColors
        });
      } else if (data.category === 'flowers') {
        setData({
          ...data,
          flowerColors: newColors
        });
      } else {
        setData({
          ...data,
          balloonColors: newColors
        });
      }
    } else if (colors.length < maxColors) {
      const newColors = [...colors, colorName];
      if (data.category === 'giftbox') {
        setData({
          ...data,
          giftBoxBalloonColors: newColors
        });
      } else if (data.category === 'flowers') {
        setData({
          ...data,
          flowerColors: newColors
        });
      } else {
        setData({
          ...data,
          balloonColors: newColors
        });
      }
    }
  };
  const toggleGiftBoxItem = (itemName: string) => {
    if (data.giftBoxItems.includes(itemName)) {
      setData({
        ...data,
        giftBoxItems: data.giftBoxItems.filter(i => i !== itemName)
      });
    } else {
      setData({
        ...data,
        giftBoxItems: [...data.giftBoxItems, itemName]
      });
    }
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const maxImages = 3;
    const remainingSlots = maxImages - data.referenceImages.length;
    if (remainingSlots <= 0) {
      alert('You can only upload up to 3 images');
      return;
    }
    const filesToProcess = Array.from(files).slice(0, remainingSlots);
    filesToProcess.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        alert('Each image must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setData(prev => ({
          ...prev,
          referenceImages: [...prev.referenceImages, base64String]
        }));
      };
      reader.readAsDataURL(file);
    });
  };
  const removeImage = (index: number) => {
    setData(prev => ({
      ...prev,
      referenceImages: prev.referenceImages.filter((_, i) => i !== index)
    }));
  };
  const generateCustomizationDetails = () => {
    let details = '';
    if (data.category === 'balloons') {
      details = `**Balloon Customization**\n\n`;
      details += `Colors: ${data.balloonColors.join(', ')}\n`;
      if (data.balloonMessage) details += `Message: "${data.balloonMessage}"\n`;
      details += `Type: ${data.balloonType === 'helium' ? 'Helium (floating)' : 'On stick'}\n`;
    } else if (data.category === 'flowers') {
      details = `**Flower Arrangement Customization**\n\n`;
      details += `Type: ${data.flowerType || 'Mixed'}\n`;
      details += `Colors: ${data.flowerColors.join(', ')}\n`;
      details += `Presentation: ${data.flowerInBox ? 'In a box' : 'Bouquet'}\n`;
      if (data.flowerInBox && data.flowerBoxMessage) {
        details += `Box message: "${data.flowerBoxMessage}"\n`;
      }
    } else if (data.category === 'giftbox') {
      details = `**Gift Box Customization**\n\n`;
      details += `For: ${data.giftBoxGender === 'man' ? 'Him' : 'Her'}\n`;
      if (data.giftBoxWithBalloons) {
        details += `\nWith Balloons:\n`;
        details += `- Colors: ${data.giftBoxBalloonColors.join(', ')}\n`;
        if (data.giftBoxBalloonMessage) {
          details += `- Message: "${data.giftBoxBalloonMessage}"\n`;
        }
      }
      if (data.giftBoxItems.length > 0) {
        details += `\nItems to include:\n`;
        data.giftBoxItems.forEach(item => {
          details += `- ${item}\n`;
        });
      }
    }
    if (data.referenceImages.length > 0) {
      details += `\n**Reference Images:** ${data.referenceImages.length} image(s) attached\n`;
    }
    if (data.recipientType === 'gifting' && data.recipientName) {
      details += `\n**Gift Recipient:**\n`;
      details += `Name: ${data.recipientName}\n`;
      if (data.recipientPhone) details += `Phone: ${data.recipientPhone}\n`;
    }
    return details;
  };
  const handleSubmitRequest = async () => {
    if (!isAuthenticated || !user) {
      sessionStorage.setItem('pendingCustomization', JSON.stringify(data));
      navigate('/login?redirect=/occasions');
      return;
    }
    setIsSubmitting(true);
    try {
      addCustomOrderRequest({
        userId: user.id,
        customerName: user.name,
        customerEmail: user.email,
        customerPhone: user.cellNumber,
        category: data.category!,
        recipientType: data.recipientType!,
        recipientName: data.recipientName,
        recipientPhone: data.recipientPhone,
        customizationDetails: generateCustomizationDetails(),
        referenceImages: data.referenceImages,
        status: 'pending'
      });
      alert('✅ Custom order request submitted! Our team will review your request and send you a quote via email.');
      onClose();
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting custom order:', error);
      alert('Failed to submit custom order request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const isStepValid = () => {
    if (step === 1) return data.category !== null;
    if (step === 2) {
      if (data.recipientType === null) return false;
      if (data.recipientType === 'gifting') {
        return data.recipientName && data.recipientPhone;
      }
      return true;
    }
    // Photo upload step is always optional, so always valid
    const photoUploadStep = data.category === 'balloons' ? 5 : data.category === 'flowers' ? 6 : data.category === 'giftbox' ? 7 : -1;
    if (step === photoUploadStep) return true;
    if (data.category === 'balloons') {
      if (step === 3) return data.balloonColors.length === 3;
      if (step === 4) return data.balloonType !== null;
    } else if (data.category === 'flowers') {
      if (step === 3) return data.flowerInBox !== null;
      if (step === 4) return data.flowerType !== undefined;
      if (step === 5) return data.flowerColors.length > 0;
    } else if (data.category === 'giftbox') {
      if (step === 3) return data.giftBoxGender !== null;
      if (step === 4) return data.giftBoxWithBalloons !== null;
      if (step === 5) {
        if (data.giftBoxWithBalloons) {
          return data.giftBoxBalloonColors.length === 3;
        }
        return true;
      }
      if (step === 6) return data.giftBoxItems.length > 0;
    }
    return true;
  };
  if (!isOpen) return null;
  // Determine if current step is the photo upload step
  const isPhotoUploadStep = data.category === 'balloons' && step === 5 || data.category === 'flowers' && step === 6 || data.category === 'giftbox' && step === 7;
  return <AnimatePresence data-id="element-724">
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-id="element-725">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} className="bg-white rounded-3xl w-full max-w-3xl flex flex-col shadow-2xl" style={{
        maxHeight: '90vh'
      }} data-id="element-726">
          {/* Header */}
          <div className="bg-gradient-to-r from-rose-500 to-purple-600 p-6 text-white relative flex-shrink-0" data-id="element-727">
            <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors" data-id="element-728">
              <X size={20} data-id="element-729" />
            </button>

            <div className="flex items-center gap-3 mb-4" data-id="element-730">
              <Sparkles className="w-6 h-6" data-id="element-731" />
              <h2 className="text-2xl font-bold" data-id="element-732">Customize Your Gift</h2>
            </div>

            {/* Progress Bar */}
            <div className="flex items-center gap-2" data-id="element-733">
              {Array.from({
              length: totalSteps
            }).map((_, i) => <div key={i} className={`flex-1 h-2 rounded-full transition-all ${i + 1 <= step ? 'bg-white' : 'bg-white/30'}`} data-id="element-734" />)}
            </div>
            <p className="text-sm text-white/80 mt-2" data-id="element-735">
              Step {step} of {totalSteps}
            </p>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-8" data-id="element-736">
            <AnimatePresence mode="wait" data-id="element-737">
              {/* Step 1: Choose Category */}
              {step === 1 && <motion.div key="step1" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} data-id="element-738">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-739">
                    What would you like to customize?
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-740">
                    Choose a category to get started
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-741">
                    {[{
                  value: 'balloons',
                  label: 'Balloons',
                  emoji: '🎈'
                }, {
                  value: 'flowers',
                  label: 'Flowers',
                  emoji: '💐'
                }, {
                  value: 'giftbox',
                  label: 'Gift Box',
                  emoji: '🎁'
                }].map(cat => <button key={cat.value} onClick={() => setData({
                  ...data,
                  category: cat.value as Category
                })} className={`p-6 rounded-2xl border-2 transition-all ${data.category === cat.value ? 'border-rose-500 bg-rose-50 shadow-lg scale-105' : 'border-gray-200 hover:border-rose-300 hover:shadow-md'}`} data-id="element-742">
                        <div className="text-4xl mb-3" data-id="element-743">{cat.emoji}</div>
                        <h4 className="font-bold text-gray-900" data-id="element-744">{cat.label}</h4>
                      </button>)}
                  </div>
                </motion.div>}

              {/* Step 2: Recipient */}
              {step === 2 && <motion.div key="step2" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} data-id="element-745">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-746">
                    Who is this gift for?
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-747">
                    Let us know who will receive this special gift
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-748">
                    <button onClick={() => setData({
                  ...data,
                  recipientType: 'myself'
                })} className={`p-6 rounded-2xl border-2 transition-all ${data.recipientType === 'myself' ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`} data-id="element-749">
                      <User className="w-8 h-8 mx-auto mb-2 text-rose-500" data-id="element-750" />
                      <h4 className="font-bold" data-id="element-751">For Myself</h4>
                    </button>
                    <button onClick={() => setData({
                  ...data,
                  recipientType: 'gifting'
                })} className={`p-6 rounded-2xl border-2 transition-all ${data.recipientType === 'gifting' ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`} data-id="element-752">
                      <Gift className="w-8 h-8 mx-auto mb-2 text-rose-500" data-id="element-753" />
                      <h4 className="font-bold" data-id="element-754">Gifting</h4>
                    </button>
                  </div>

                  {data.recipientType === 'gifting' && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="space-y-4" data-id="element-755">
                      <Input label="Recipient Name" value={data.recipientName || ''} onChange={e => setData({
                  ...data,
                  recipientName: e.target.value
                })} placeholder="Enter recipient's name" required data-id="element-756" />
                      <Input label="Recipient Phone Number" value={data.recipientPhone || ''} onChange={e => setData({
                  ...data,
                  recipientPhone: e.target.value
                })} placeholder="Enter phone number" type="tel" required data-id="element-757" />
                    </motion.div>}
                </motion.div>}

              {/* Balloon Steps */}
              {data.category === 'balloons' && step === 3 && <motion.div key="balloon-colors" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} data-id="element-758">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-759">
                    Choose 3 Balloon Colors
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-760">
                    Selected: {data.balloonColors.length}/3
                  </p>

                  <div className="grid grid-cols-4 gap-3" data-id="element-761">
                    {BALLOON_COLORS.map(color => <button key={color.name} onClick={() => toggleColor(color.name)} className={`p-4 rounded-xl border-2 transition-all ${data.balloonColors.includes(color.name) ? 'border-rose-500 ring-2 ring-rose-200' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-762">
                        <div className="w-12 h-12 rounded-full mx-auto mb-2 border-2 border-gray-200" style={{
                    backgroundColor: color.hex
                  }} data-id="element-763" />
                        <p className="text-xs font-medium text-gray-700" data-id="element-764">
                          {color.name}
                        </p>
                        {data.balloonColors.includes(color.name) && <Check className="w-4 h-4 text-rose-500 mx-auto mt-1" data-id="element-765" />}
                      </button>)}
                  </div>
                </motion.div>}

              {data.category === 'balloons' && step === 4 && <motion.div key="balloon-details" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-766">
                  <div data-id="element-767">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-768">
                      Balloon Details
                    </h3>
                    <p className="text-gray-600 mb-6" data-id="element-769">
                      Add a message and choose balloon type
                    </p>

                    <Input label="Message on Balloon (Optional)" value={data.balloonMessage || ''} onChange={e => setData({
                  ...data,
                  balloonMessage: e.target.value
                })} placeholder="e.g., Happy Birthday!" maxLength={50} data-id="element-770" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-771">
                      {data.balloonMessage?.length || 0}/50 characters
                    </p>
                  </div>

                  <div data-id="element-772">
                    <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-773">
                      Balloon Type
                    </label>
                    <div className="grid grid-cols-2 gap-4" data-id="element-774">
                      <button onClick={() => setData({
                    ...data,
                    balloonType: 'helium'
                  })} className={`p-6 rounded-2xl border-2 transition-all ${data.balloonType === 'helium' ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`} data-id="element-775">
                        <div className="text-3xl mb-2" data-id="element-776">🎈</div>
                        <h4 className="font-bold mb-1" data-id="element-777">Helium</h4>
                        <p className="text-sm text-gray-600" data-id="element-778">
                          Floats in the air
                        </p>
                      </button>
                      <button onClick={() => setData({
                    ...data,
                    balloonType: 'stick'
                  })} className={`p-6 rounded-2xl border-2 transition-all ${data.balloonType === 'stick' ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`} data-id="element-779">
                        <div className="text-3xl mb-2" data-id="element-780">🎯</div>
                        <h4 className="font-bold mb-1" data-id="element-781">On Stick</h4>
                        <p className="text-sm text-gray-600" data-id="element-782">
                          Attached to stick
                        </p>
                      </button>
                    </div>
                  </div>
                </motion.div>}

              {/* Photo Upload Step - Shows as last step for all categories */}
              {isPhotoUploadStep && <motion.div key="photo-upload" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} data-id="element-783">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-784">
                    Add Reference Photos (Optional)
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-785">
                    Upload up to 3 photos to help us understand your vision
                    better
                  </p>

                  {/* Upload Area */}
                  <div className="mb-6" data-id="element-786">
                    <label htmlFor="image-upload" className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-2xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-787">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6" data-id="element-788">
                        <Upload className="w-12 h-12 text-gray-400 mb-3" data-id="element-789" />
                        <p className="mb-2 text-sm text-gray-500" data-id="element-790">
                          <span className="font-semibold" data-id="element-791">Click to upload</span>{' '}
                          or drag and drop
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-792">
                          PNG, JPG or JPEG (Max 5MB per image)
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-793">
                          {data.referenceImages.length}/3 images uploaded
                        </p>
                      </div>
                      <input id="image-upload" type="file" className="hidden" accept="image/png,image/jpeg,image/jpg" multiple onChange={handleImageUpload} disabled={data.referenceImages.length >= 3} data-id="element-794" />
                    </label>
                  </div>

                  {/* Image Preview Grid */}
                  {data.referenceImages.length > 0 && <div className="grid grid-cols-3 gap-4" data-id="element-795">
                      {data.referenceImages.map((image, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} className="relative group aspect-square rounded-xl overflow-hidden border-2 border-gray-200" data-id="element-796">
                          <img src={image} alt={`Reference ${index + 1}`} className="w-full h-full object-cover" data-id="element-797" />
                          <button onClick={() => removeImage(index)} className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600" data-id="element-798">
                            <Trash2 className="w-4 h-4" data-id="element-799" />
                          </button>
                        </motion.div>)}
                    </div>}

                  <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200" data-id="element-800">
                    <div className="flex items-start gap-3" data-id="element-801">
                      <ImageIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" data-id="element-802" />
                      <div className="text-sm text-blue-800" data-id="element-803">
                        <p className="font-medium mb-1" data-id="element-804">
                          💡 Tips for better results:
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-805">
                          <li data-id="element-806">Upload clear, well-lit photos</li>
                          <li data-id="element-807">Show different angles if possible</li>
                          <li data-id="element-808">
                            Include any specific details you want replicated
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>}

              {/* Flower Steps */}
              {data.category === 'flowers' && step === 3 && <motion.div key="flower-box" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} data-id="element-809">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-810">
                    Flower Presentation
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-811">
                    Would you like your flowers in a box?
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-812">
                    <button onClick={() => setData({
                  ...data,
                  flowerInBox: true
                })} className={`p-6 rounded-2xl border-2 transition-all ${data.flowerInBox === true ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`} data-id="element-813">
                      <div className="text-3xl mb-2" data-id="element-814">📦</div>
                      <h4 className="font-bold mb-1" data-id="element-815">In a Box</h4>
                      <p className="text-sm text-gray-600" data-id="element-816">
                        Elegant presentation
                      </p>
                    </button>
                    <button onClick={() => setData({
                  ...data,
                  flowerInBox: false
                })} className={`p-6 rounded-2xl border-2 transition-all ${data.flowerInBox === false ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`} data-id="element-817">
                      <div className="text-3xl mb-2" data-id="element-818">💐</div>
                      <h4 className="font-bold mb-1" data-id="element-819">Bouquet</h4>
                      <p className="text-sm text-gray-600" data-id="element-820">Traditional wrap</p>
                    </button>
                  </div>

                  {data.flowerInBox && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} data-id="element-821">
                      <Input label="Message on Box (Optional)" value={data.flowerBoxMessage || ''} onChange={e => setData({
                  ...data,
                  flowerBoxMessage: e.target.value
                })} placeholder="e.g., With Love" maxLength={30} data-id="element-822" />
                    </motion.div>}
                </motion.div>}

              {data.category === 'flowers' && step === 4 && <motion.div key="flower-type" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} data-id="element-823">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-824">
                    Choose Flower Type
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-825">
                    Select your preferred flowers
                  </p>

                  <div className="grid grid-cols-2 gap-3" data-id="element-826">
                    {FLOWER_TYPES.map(type => <button key={type} onClick={() => setData({
                  ...data,
                  flowerType: type
                })} className={`p-4 rounded-xl border-2 transition-all text-left ${data.flowerType === type ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`} data-id="element-827">
                        <h4 className="font-bold text-gray-900" data-id="element-828">{type}</h4>
                        {data.flowerType === type && <Check className="w-4 h-4 text-rose-500 mt-2" data-id="element-829" />}
                      </button>)}
                  </div>
                </motion.div>}

              {data.category === 'flowers' && step === 5 && <motion.div key="flower-colors" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} data-id="element-830">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-831">
                    Choose Flower Colors
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-832">
                    Select one or more colors (up to 3)
                  </p>

                  <div className="grid grid-cols-4 gap-3" data-id="element-833">
                    {BALLOON_COLORS.filter(c => !['Black', 'Silver', 'Gold'].includes(c.name)).map(color => <button key={color.name} onClick={() => toggleColor(color.name, 3)} className={`p-4 rounded-xl border-2 transition-all ${data.flowerColors.includes(color.name) ? 'border-rose-500 ring-2 ring-rose-200' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-834">
                        <div className="w-12 h-12 rounded-full mx-auto mb-2 border-2 border-gray-200" style={{
                    backgroundColor: color.hex
                  }} data-id="element-835" />
                        <p className="text-xs font-medium text-gray-700" data-id="element-836">
                          {color.name}
                        </p>
                        {data.flowerColors.includes(color.name) && <Check className="w-4 h-4 text-rose-500 mx-auto mt-1" data-id="element-837" />}
                      </button>)}
                  </div>
                </motion.div>}

              {/* Gift Box Steps */}
              {data.category === 'giftbox' && step === 3 && <motion.div key="giftbox-gender" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} data-id="element-838">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-839">
                    Who is this gift box for?
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-840">
                    This helps us curate the perfect items
                  </p>

                  <div className="grid grid-cols-2 gap-4" data-id="element-841">
                    <button onClick={() => setData({
                  ...data,
                  giftBoxGender: 'man'
                })} className={`p-8 rounded-2xl border-2 transition-all ${data.giftBoxGender === 'man' ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`} data-id="element-842">
                      <div className="text-4xl mb-3" data-id="element-843">👨</div>
                      <h4 className="font-bold text-lg" data-id="element-844">For Him</h4>
                    </button>
                    <button onClick={() => setData({
                  ...data,
                  giftBoxGender: 'woman'
                })} className={`p-8 rounded-2xl border-2 transition-all ${data.giftBoxGender === 'woman' ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`} data-id="element-845">
                      <div className="text-4xl mb-3" data-id="element-846">👩</div>
                      <h4 className="font-bold text-lg" data-id="element-847">For Her</h4>
                    </button>
                  </div>
                </motion.div>}

              {data.category === 'giftbox' && step === 4 && <motion.div key="giftbox-balloons" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} data-id="element-848">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-849">
                    Add Balloons?
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-850">
                    Make it extra special with balloons
                  </p>

                  <div className="grid grid-cols-2 gap-4" data-id="element-851">
                    <button onClick={() => setData({
                  ...data,
                  giftBoxWithBalloons: true
                })} className={`p-6 rounded-2xl border-2 transition-all ${data.giftBoxWithBalloons === true ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`} data-id="element-852">
                      <div className="text-3xl mb-2" data-id="element-853">🎈</div>
                      <h4 className="font-bold mb-1" data-id="element-854">With Balloons</h4>
                      <p className="text-sm text-gray-600" data-id="element-855">Add 3 balloons</p>
                    </button>
                    <button onClick={() => setData({
                  ...data,
                  giftBoxWithBalloons: false
                })} className={`p-6 rounded-2xl border-2 transition-all ${data.giftBoxWithBalloons === false ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`} data-id="element-856">
                      <div className="text-3xl mb-2" data-id="element-857">📦</div>
                      <h4 className="font-bold mb-1" data-id="element-858">No Balloons</h4>
                      <p className="text-sm text-gray-600" data-id="element-859">Gift box only</p>
                    </button>
                  </div>
                </motion.div>}

              {data.category === 'giftbox' && step === 5 && data.giftBoxWithBalloons && <motion.div key="giftbox-balloon-details" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-860">
                    <div data-id="element-861">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-862">
                        Balloon Details
                      </h3>
                      <p className="text-gray-600 mb-6" data-id="element-863">
                        Choose 3 colors: {data.giftBoxBalloonColors.length}/3
                      </p>

                      <div className="grid grid-cols-4 gap-3 mb-6" data-id="element-864">
                        {BALLOON_COLORS.map(color => <button key={color.name} onClick={() => toggleColor(color.name, 3)} className={`p-4 rounded-xl border-2 transition-all ${data.giftBoxBalloonColors.includes(color.name) ? 'border-rose-500 ring-2 ring-rose-200' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-865">
                            <div className="w-12 h-12 rounded-full mx-auto mb-2 border-2 border-gray-200" style={{
                      backgroundColor: color.hex
                    }} data-id="element-866" />
                            <p className="text-xs font-medium text-gray-700" data-id="element-867">
                              {color.name}
                            </p>
                            {data.giftBoxBalloonColors.includes(color.name) && <Check className="w-4 h-4 text-rose-500 mx-auto mt-1" data-id="element-868" />}
                          </button>)}
                      </div>
                    </div>

                    <Input label="Message on Balloons (Optional)" value={data.giftBoxBalloonMessage || ''} onChange={e => setData({
                ...data,
                giftBoxBalloonMessage: e.target.value
              })} placeholder="e.g., Congratulations!" maxLength={50} data-id="element-869" />
                  </motion.div>}

              {data.category === 'giftbox' && (step === 5 && !data.giftBoxWithBalloons || step === 6) && <motion.div key="giftbox-items" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} data-id="element-870">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-871">
                      Select Items for Gift Box
                    </h3>
                    <p className="text-gray-600 mb-6" data-id="element-872">
                      Choose items you'd like included (we'll provide a custom
                      quote)
                    </p>

                    <div className="grid grid-cols-2 gap-3" data-id="element-873">
                      {GIFT_BOX_ITEMS.map(item => <button key={item} onClick={() => toggleGiftBoxItem(item)} className={`p-4 rounded-xl border-2 transition-all text-left ${data.giftBoxItems.includes(item) ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`} data-id="element-874">
                          <div className="flex items-start justify-between" data-id="element-875">
                            <h4 className="font-bold text-gray-900" data-id="element-876">{item}</h4>
                            {data.giftBoxItems.includes(item) && <Check className="w-5 h-5 text-rose-500" data-id="element-877" />}
                          </div>
                        </button>)}
                    </div>

                    <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200" data-id="element-878">
                      <p className="text-sm text-blue-800" data-id="element-879">
                        <strong data-id="element-880">📋 Note:</strong> Our team will review your
                        selections and send you a personalized quote via email.
                      </p>
                    </div>
                  </motion.div>}
            </AnimatePresence>
          </div>

          {/* Footer - Fixed at bottom */}
          <div className="p-6 bg-gray-50 border-t border-gray-200 flex-shrink-0" data-id="element-881">
            {step === totalSteps && <div className="mb-4 p-4 bg-gradient-to-r from-rose-50 to-purple-50 rounded-xl border border-rose-200" data-id="element-882">
                <p className="text-sm text-gray-700 text-center" data-id="element-883">
                  <strong data-id="element-884">✨ Almost there!</strong> Submit your request and
                  we'll send you a custom quote within 24 hours.
                </p>
              </div>}

            <div className="flex gap-3" data-id="element-885">
              {step > 1 && <Button variant="outline" onClick={handleBack} leftIcon={<ChevronLeft data-id="element-887" />} className="flex-1" data-id="element-886">
                  Back
                </Button>}
              {step < totalSteps ? <Button onClick={handleNext} disabled={!isStepValid()} rightIcon={<ChevronRight data-id="element-889" />} className="flex-1" data-id="element-888">
                  Next
                </Button> : <Button onClick={handleSubmitRequest} disabled={!isStepValid() || isSubmitting} rightIcon={<Send data-id="element-891" />} className="flex-1" data-id="element-890">
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}